<template>
  <div
    class="fixed top-[36px] sm:top-[38px] left-0 right-0 bg-[var(--color-8)] dark:bg-transparent dark:bg-[linear-gradient(287.24deg,#1F0432_2.25%,#1D0231_93.17%)] z-50 border-t border-[#8a36c230] shadow-[0px_4px_10px_-3px_rgba(147,_51,_234,_0.4)] dark:border-[#8a36c265] dark:shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
    :class="appendClass"
    @mouseover="handleMouseOver"
    @mouseout="handleMouseOut"
  >
    <nav
      class="text-[var(--secondary-foreground)] max-lg:py-3 relative border-0 bg-transparent max-w-screen-xl px-4 mx-auto border-[#250243] flex items-center"
    >
      <!-- Logo Section -->
      <div class="flex max-lg:justify-between max-lg:w-full">
        <NuxtLink
          to="/"
          aria-label="wri-logo"
          class="flex items-center text-3xl"
        >
          <img
            alt="webreinvent logo"
            src="/images/wri-logo-v1.gif"
            width="50"
            height="39"
            loading="eager"
            fetchpriority="high"
            class="mr-1"
          />
          <span v-if="isDesktop" class="text-[var(--foreground)]">
            <span class="border-l pl-2 border-[#B88ABE50] mt-1 font-semibold"
              >web</span
            >reinvent
          </span>
        </NuxtLink>
      </div>

      <!-- Desktop Navigation -->
      <ul
        class="text-[var(--secondary-foreground)] font-semibold dark:font-normal max-lg:peer-aria-expanded:flex max-lg:hidden max-lg:absolute max-lg:top-16 top-2 max-lg:pb-4 max-lg:w-full max-lg:bg-[var(--color-8)] max-lg:border-b border-[#501D71] dark:border-0 max-lg:left-0 flex flex-col lg:flex-row ml-auto min-w-max"
        :class="{
          'max-lg:flex': isMobileMenuOpen,
          'max-lg:hidden': !isMobileMenuOpen,
        }"
      >
        <li v-for="item in items" :key="item.label" class="group">
          <!-- Services Item (with MegaMenu) -->
          <div
            @mouseenter="handleFrameworkMouseEnter"
            @mouseleave="handleFrameworkMouseLeave"
            v-if="item.label === 'Services'"
            class="flex items-center py-2.5 lg:py-[20px] pl-8"
          >
            <NuxtLink :to="item.link" class="flex items-center">
              <span class="text-sm font-semibold">{{ item.label }}</span>
              <Badge v-if="item.badge" class="ml-2" :value="item.badge" />
            </NuxtLink>
            <button
              class="px-2 py-3.5 cursor-pointer transition-transform duration-200 hover:bg-[var(--accent)] rounded"
              :class="{ 'rotate-180': isMegaMenuOpen }"
              aria-label="Toggle menu"
            >
              <svg
                width="11"
                height="6"
                viewBox="0 0 11 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.5 0.5L5.5 5.5L10.5 0.5"
                  stroke="currentColor"
                  stroke-width="0.833333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <LazyWrMegaMenu
              v-if="isDesktop"
              :isOpen="isMegaMenuOpen"
              @mouseenter="handleMegaMenuMouseEnter"
              @mouseleave="handleMegaMenuMouseLeave"
              @closeMegamenu="handleMegaMenuClose"
            />
          </div>

          <!-- AI Item (with MegaMenu) -->
          <div
            @mouseenter="handleAiMouseEnter"
            @mouseleave="handleAiMouseLeave"
            v-else-if="item.label === 'AI'"
            class="flex items-center py-2.5 lg:py-[20px] pl-8"
          >
            <NuxtLink :to="item.link" class="flex items-center">
              <span class="text-sm font-semibold">{{ item.label }}</span>
              <Badge v-if="item.badge" class="ml-2" :value="item.badge" />
            </NuxtLink>
            <button
              class="px-2 py-3.5 cursor-pointer transition-transform duration-200 hover:bg-[var(--accent)] rounded"
              :class="{ 'rotate-180': isAiMegaMenuOpen }"
              aria-label="Toggle menu"
            >
              <svg
                width="11"
                height="6"
                viewBox="0 0 11 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.5 0.5L5.5 5.5L10.5 0.5"
                  stroke="currentColor"
                  stroke-width="0.833333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <LazyWrAiMegaMenu
              v-if="isDesktop"
              :isOpen="isAiMegaMenuOpen"
              @mouseenter="handleAiMegaMenuMouseEnter"
              @mouseleave="handleAiMegaMenuMouseLeave"
              @closeMegamenu="handleAiMegaMenuClose"
            />
          </div>

          <!-- Company, Solutions, Products Items (with Dropdown) -->
          <div
            @mouseenter="() => handleDropdownMouseEnter(item.label)"
            @mouseleave="handleDropdownMouseLeave"
            v-else-if="
              ['Company', 'Solutions', 'Products'].includes(item.label)
            "
            class="flex items-center lg:py-[20px] relative pl-8 gap-2"
          >
            <!-- Menu Item Link (No hover events for dropdown) -->
            <NuxtLink
              :to="item.link"
              class="flex items-center hover:text-[var(--primary-color)] transition-colors"
            >
              <span class="text-sm font-semibold">{{ item.label }}</span>
              <Badge v-if="item.badge" class="ml-2" :value="item.badge" />
            </NuxtLink>

            <!-- Dropdown Icon Button (Only this triggers dropdown) -->
            <button
              v-if="item.items && item.items.length > 0"
              class="py-3.5 cursor-pointer transition-all duration-200 hover:bg-[var(--accent)] rounded"
              aria-label="Toggle menu"
              :class="{
                'rotate-180':
                  activeDropdownLabel === item.label && isDropdownOpen,
              }"
            >
              <svg
                width="11"
                height="6"
                viewBox="0 0 11 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.5 0.5L5.5 5.5L10.5 0.5"
                  stroke="currentColor"
                  stroke-width="0.833333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <!-- Dropdown Component -->

            <WrDropDown
              v-if="item.items && item.items.length > 0"
              :last-link="item.lastLink"
              :label="item.label"
              :items="item.items"
              :isOpen="activeDropdownLabel === item.label && isDropdownOpen"
              viewAllLink=""
              @mouseenter="handleDropdownContentMouseEnter"
              @mouseleave="handleDropdownMouseLeave"
              @close-dropdown="handleCloseDropDownAndRemoveClass"
            />
          </div>

          <!-- Regular Items (Home, etc.) -->
          <div v-else class="">
            <NuxtLink
              :to="item.link"
              class="flex items-center cursor-pointer px-1.5 py-2.5 lg:py-[27px] hover:text-[var(--primary-color)] transition-colors"
            >
              <span class="text-sm font-semibold">{{ item.label }}</span>
              <Badge v-if="item.badge" class="ml-2" :value="item.badge" />
            </NuxtLink>
          </div>
        </li>
      </ul>

      <!-- Desktop Navigation Items (Right side) -->
      <div class="ml-0 min-w-max">
        <WrNavitems />
      </div>
    </nav>
  </div>
</template>

<script setup>
defineProps({
  appendClass: {
    type: String,
    default: "",
  },
});

const { isDesktop } = useDevice();
import { ref, computed } from "vue";
import { useIosDevice } from "@/composables/useIosDevice";
import { defineAsyncComponent } from "vue";

// Molecules
const LazyWrMegaMenu = defineAsyncComponent(
  () => import("../molecules/WrMegaMenu.vue"),
);
const LazyWrAiMegaMenu = defineAsyncComponent(
  () => import("../molecules/WrAiMegaMenu.vue"),
);
const WrDropDown = defineAsyncComponent(
  () => import("../molecules/WrDropDown.vue"),
);
const WrNavitems = defineAsyncComponent(
  () => import("../molecules/WrNavitems.vue"),
);

// Mobile menu state
const { isIOS } = useIosDevice();
const isMobileMenuOpen = ref(false);

// MegaMenu state
const isHoveringFramework = ref(false);
const isHoveringMegaMenu = ref(false);
const shouldCloseMegaMenu = ref(false);

// AI MegaMenu state
const isHoveringAi = ref(false);
const isHoveringAiMegaMenu = ref(false);
const shouldCloseAiMegaMenu = ref(false);

// Dropdown state
const isDropdownOpen = ref(false);
const activeDropdownLabel = ref(null);
let dropdownCloseTimeout = null;

// Mobile menu toggle
function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
}

// Dropdown handlers - Only triggered by icon hover
function handleDropdownMouseEnter(label) {
  clearTimeout(dropdownCloseTimeout);
  activeDropdownLabel.value = label;
  isDropdownOpen.value = true;
}

function handleDropdownMouseLeave() {
  dropdownCloseTimeout = setTimeout(() => {
    isDropdownOpen.value = false;
    activeDropdownLabel.value = null;
  }, 150); // Reduced timeout for better UX
}

// Additional handler for when hovering over dropdown content
function handleDropdownContentMouseEnter() {
  clearTimeout(dropdownCloseTimeout);
}

const handleCloseDropDownAndRemoveClass = () => {
  console.log("close dropdown called");
  // isDropdownOpen.value = false;
  // activeDropdownLabel.value = null;
  // document.body.classList.remove("overflow-hidden");
};

onUnmounted(() => {
  if (dropdownCloseTimeout) {
    clearTimeout(dropdownCloseTimeout);
    dropdownCloseTimeout = null;
  }
  document.body.classList.remove("overflow-hidden");
});

function handleDropdownClose() {
  clearTimeout(dropdownCloseTimeout);
  isDropdownOpen.value = false;
  activeDropdownLabel.value = null;
}

// MegaMenu computed and handlers
const isMegaMenuOpen = computed(() => {
  return (
    (isHoveringFramework.value || isHoveringMegaMenu.value) &&
    !shouldCloseMegaMenu.value
  );
});

const handleFrameworkMouseEnter = () => {
  isHoveringFramework.value = true;
  shouldCloseMegaMenu.value = false;
};

const handleFrameworkMouseLeave = () => {
  isHoveringFramework.value = false;
};

const handleMegaMenuMouseEnter = () => {
  isHoveringMegaMenu.value = true;
};

const handleMegaMenuMouseLeave = () => {
  isHoveringMegaMenu.value = false;
  document.body.classList.remove("overflow-hidden");
};

const handleMegaMenuClose = () => {
  shouldCloseMegaMenu.value = true;
  isHoveringFramework.value = false;
  isHoveringMegaMenu.value = false;
  document.body.classList.remove("overflow-hidden");
};

// AI MegaMenu computed and handlers
const isAiMegaMenuOpen = computed(() => {
  return (
    (isHoveringAi.value || isHoveringAiMegaMenu.value) &&
    !shouldCloseAiMegaMenu.value
  );
});

const handleAiMouseEnter = () => {
  isHoveringAi.value = true;
  shouldCloseAiMegaMenu.value = false;
};

const handleAiMouseLeave = () => {
  isHoveringAi.value = false;
};

const handleAiMegaMenuMouseEnter = () => {
  isHoveringAiMegaMenu.value = true;
};

const handleAiMegaMenuMouseLeave = () => {
  isHoveringAiMegaMenu.value = false;
  document.body.classList.remove("overflow-hidden");
};

const handleAiMegaMenuClose = () => {
  shouldCloseAiMegaMenu.value = true;
  isHoveringAi.value = false;
  isHoveringAiMegaMenu.value = false;
  document.body.classList.remove("overflow-hidden");
};

const handleMouseOver = () => {
  if (!isDesktop) return;
  document.body.classList.add("overflow-hidden");
};

const handleMouseOut = (event) => {
  if (!isDesktop) return;
  const header = event.currentTarget;
  if (!header.contains(event.relatedTarget)) {
    document.body.classList.remove("overflow-hidden");
  }
};

watch([isDropdownOpen, isMegaMenuOpen, isAiMegaMenuOpen], ([dropdownOpen, megaMenuOpen, aiMegaOpen]) => {
  if (import.meta.client && !dropdownOpen && !megaMenuOpen && !aiMegaOpen) {
    console.log("removing overflow-hidden");
    document.body.classList.remove("overflow-hidden");
  }
});

// Products menu — built from public/content/products-content.json.
// Open source lists only the featured core platforms (the small Laravel
// utilities are dropped from the menu).
const { openSource, ai } = await useProductsData();

const productMenuItems = computed(() => [
  {
    label: "AI Ecosystem",
    link: "/products/#productsAi",
    items: ai.value.map((p) => ({
      label: `<strong>${p.name}</strong>`,
      link: p.link,
      iconURL: p.icon || "",
    })),
  },
  {
    label: "Open Source",
    link: "/products/#productsOpenSource",
    items: openSource.value
      .filter((p) => p.featured)
      .map((p) => ({
        label: `${p.prefix}<strong>${p.name}</strong>`,
        link: `/products/#${p.id}`,
        iconURL: p.icon || "",
      })),
  },
]);

// Navigation items
const items = computed(() => [
  {
    label: "Home",
    link: "/",
  },
  {
    label: "AI",
    link: "/ai",
    items: [],
  },
  {
    label: "Services",
    link: "/services",
    items: [],
  },
  {
    label: "Solutions",
    link: "/solutions",
    lastLink: true,
    items: [
      { label: "CRM", link: "/solutions/crm-development-services" },
      { label: "ERP", link: "/solutions/erp-development-services" },
      {
        label: "Survey Software",
        link: "/solutions/survey-software-development-services",
      },
      {
        label: "Project Management System",
        link: "/solutions/project-management-systems-development-services",
      },
      {
        label: "Restaurant Management Software",
        link: "/solutions/restaurant-management-software-development-services",
      },
      {
        label: "Warehouse Automation",
        link: "/solutions/warehouse-automation-development-services",
      },
      {
        label: "Time Tracking Software",
        link: "/solutions/time-tracking-software-development-services",
      },
      {
        label: "Visitor Management Software",
        link: "/solutions/visitor-management-software-development-services",
      },
      {
        label: "Inventory Management System",
        link: "/solutions/inventory-management-systems-development-services",
      },
      {
        label: "FAS",
        link: "/solutions/fixed-asset-management-accounting-software-fas-development-services",
      },
      { label: "View All", link: "/solutions" },
    ],
  },
  {
    label: "Products",
    link: "/products",
    viewAllLink: "/products",
    items: productMenuItems.value,
  },
  {
    label: "Company",
    link: "/company",
    lastLink: true,
    items: [
      { label: "About Us", link: "/about-us" },
      { label: "Case Studies", link: "/case-studies" },
      { label: "How We Work", link: "/how-we-work" },
      { label: "Capabilities", link: "/capabilities" },
      { label: "Dedicated Team", link: "/dedicated-team" },
      { label: "Staff Augmentation", link: "/staff-augmentation" },
      { label: "Testimonials", link: "/testimonials" },
      { label: "Career", link: "/career" },
    ],
  },
]);
</script>
